import { ToastType } from '../types';
import { MAX_VISIBLE_TOASTS, TOAST_DURATIONS } from '../constants/defaults';
import { escapeHtml } from '../utils/sanitizer';

interface ToastEntry {
  id: number;
  message: string;
  type: ToastType;
  element: HTMLElement;
  timerId: ReturnType<typeof setTimeout>;
}

export class ToastService {
  private container: HTMLElement | null = null;
  private toasts: ToastEntry[] = [];
  private nextId: number = 0;

  initialize(container?: HTMLElement): void {
    if (container) {
      this.container = container;
    } else {
      this.container = document.getElementById('toast-container');
      if (!this.container) {
        this.container = document.createElement('div');
        this.container.id = 'toast-container';
        this.container.className = 'toast-container';
        this.container.setAttribute('aria-live', 'polite');
        this.container.setAttribute('aria-atomic', 'false');
        document.body.appendChild(this.container);
      }
    }
  }

  show(message: string, type: ToastType = 'info'): void {
    if (!this.container) this.initialize();

    // Enforce max visible toasts
    while (this.toasts.length >= MAX_VISIBLE_TOASTS) {
      this.dismissOldest();
    }

    const id = this.nextId++;
    const element = this.createToastElement(message, type, id);
    this.container!.appendChild(element);

    // Trigger animation
    requestAnimationFrame(() => {
      element.classList.add('toast--visible');
    });

    const duration = TOAST_DURATIONS[type] || 4000;
    const timerId = setTimeout(() => {
      this.dismiss(id);
    }, duration);

    this.toasts.push({ id, message, type, element, timerId });
  }

  dismissAll(): void {
    const toastsCopy = [...this.toasts];
    for (const toast of toastsCopy) {
      this.dismiss(toast.id);
    }
  }

  private dismiss(id: number): void {
    const index = this.toasts.findIndex((t) => t.id === id);
    if (index === -1) return;

    const toast = this.toasts[index];
    clearTimeout(toast.timerId);
    toast.element.classList.remove('toast--visible');
    toast.element.classList.add('toast--fade-out');

    setTimeout(() => {
      toast.element.remove();
    }, 300);

    this.toasts.splice(index, 1);
  }

  private dismissOldest(): void {
    if (this.toasts.length === 0) return;
    this.dismiss(this.toasts[0].id);
  }

  private createToastElement(message: string, type: ToastType, id: number): HTMLElement {
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.setAttribute('role', 'alert');
    toast.dataset.toastId = String(id);

    const icon = this.getIcon(type);
    toast.innerHTML = `
      <span class="toast__icon">${icon}</span>
      <span class="toast__message">${escapeHtml(message)}</span>
      <button class="toast__close" aria-label="Close" type="button">&times;</button>
    `;

    const closeBtn = toast.querySelector('.toast__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.dismiss(id));
    }

    return toast;
  }

  private getIcon(type: ToastType): string {
    switch (type) {
      case 'success': return '✓';
      case 'info': return 'ℹ';
      case 'warning': return '⚠';
      case 'error': return '✗';
    }
  }
}
